import React, { memo } from 'react';

import i18n from 'Utilities/i18n';

type CommonProps = {
  element?: React.ElementType;
} & React.HTMLProps<HTMLElement>;

type WithTransProps = {
  translation: string;
};
type WithTransKeyProps = {
  translationKey: string;
  translationProps?: any;
};

type HtmlTransProps = (WithTransProps | WithTransKeyProps) & CommonProps;

/**
 * This is a component that allows you to put a translation on the screen that includes html
 * elements (such as a <strong> tag) and have those elements rendered in the DOM.
 * React has a very specific way to set html with a string in this way, as you can see below.
 *
 * Props that you must pass in:
 * - either 'translation' which is the string you want to draw
 * - or 'translationKey' which is the i18n key to use
 *
 * optional props:
 * - translationProps - the object that you want to us to interpolate values into the translated string
 * - element - defaults to span - this is a string that represents the html element you want to draw the string inside of
 *
 * all other props:
 * - will be passed to the element that you are drawing
 */
const HtmlTranslation = memo(function HtmlTranslation({ element, ...props }: HtmlTransProps) {
  let translated = '';
  let passThrough: React.HTMLProps<HTMLElement>;

  if ('translation' in props) {
    const { translation, ...other } = props;

    translated = translation;
    passThrough = other;
  } else {
    const { translationKey, translationProps, ...other } = props;

    translated = (i18n.t(translationKey, translationProps) as unknown) as string;
    passThrough = other;
  }

  return React.createElement(element || 'span', {
    dangerouslySetInnerHTML: { __html: translated },
    ...passThrough,
  });
});

type InputTransProps = {
  components: { [match: string]: React.ReactNode };
} & HtmlTransProps;

/**
 * This is a component that allows you to interpolate entire components in your translated string.
 * The translated string indicates where is wants componets using words surrounded by double carets:
 *
 * "I want an ^^input^^ right there"
 *
 * You will pass in a prop called components that is an object where the keys match the words inside
 * of the double carets.
 *
 * For example, the above string you would have an object like:
 *
 * {
 *   input: <input />
 * }
 *
 * Props match everything from HtmlTranslation, but include one more necessary prop:
 * - components - an object where the keys match up to the interpolation identifiers and the values are
 *    components that you want drawn.
 */
export const InputTranslation: React.FC<React.PropsWithChildren<InputTransProps>> = ({
  components,
  ...props
}) => {
  let id = props.id;
  let translated = '';
  let passThrough: CommonProps;

  if ('translation' in props) {
    const { translation, ...other } = props;

    translated = translation;
    passThrough = other;
  } else {
    const { translationKey, translationProps, ...other } = props;

    if (!id) {
      id = translationKey;
    }

    translated = (i18n.t(translationKey, translationProps) as unknown) as string;
    passThrough = other;
  }

  const insertions = translated.matchGlobal(/\^\^(\S+)\^\^/g);

  let parts = ['', translated];
  const result: JSX.Element[] = [];

  insertions &&
    insertions.forEach((match, index) => {
      parts = parts[1].split(match[0]);
      const textPortion = parts[0];

      if (textPortion) {
        result.push(
          <HtmlTranslation key={`${id}-${index}`} {...passThrough} translation={textPortion} />
        );
      }

      result.push(
        <React.Fragment key={`${id}-component-${index}`}>{components[match[1]]}</React.Fragment>
      );
    });

  const finalBit = parts[1];

  if (finalBit) {
    result.push(<HtmlTranslation key={`${id}-last`} {...passThrough} translation={finalBit} />);
  }

  return <>{result}</>;
};

export default HtmlTranslation;
